import { useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, loadSession } from "../lib/api.js";
import EmptyState from "../components/EmptyState.js";
import Drawer from "../components/Drawer.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

type Entry = {
  id: string;
  action: string;
  actorRole: string;
  actorUserId?: string;
  occurredAt: string;
  category?: string;
  severity?: string;
  details: Record<string, unknown>;
};

const CATEGORIES = ["", "AUTHENTICATION", "AUTHORIZATION", "CONFIGURATION", "DATA", "LIFECYCLE", "OPERATIONS", "BILLING", "SECURITY"];
const SEVERITIES = ["", "info", "notice", "warning", "critical"];

function severityPill(s?: string) {
  if (s === "critical") return <span className="pill pill-err">Critical</span>;
  if (s === "warning")  return <span className="pill pill-warn">Warning</span>;
  if (s === "notice")   return <span className="pill pill-info">Notice</span>;
  return <span className="pill pill-muted">{s ?? "info"}</span>;
}

function AuditTableSection({
  entries,
  loading,
  actor,
  actionFilter,
  from,
  to,
  setDetail
}: {
  entries: Entry[];
  loading: boolean;
  actor: string;
  actionFilter: string;
  from: string;
  to: string;
  setDetail: (e: Entry) => void;
}) {
  const filtered = useMemo(() => {
    const q = actor.trim().toLowerCase();
    const actionQ = actionFilter.trim().toLowerCase();
    const fromMs = from ? Date.parse(from) : Number.NEGATIVE_INFINITY;
    const toMs = to ? Date.parse(to) : Number.POSITIVE_INFINITY;
    return entries.filter((e) => {
      if (q && !(e.actorUserId ?? "").toLowerCase().includes(q) && !e.actorRole.toLowerCase().includes(q)) return false;
      if (actionQ && !e.action.toLowerCase().includes(actionQ)) return false;
      const t = Date.parse(e.occurredAt);
      if (Number.isFinite(t) && (t < fromMs || t > toMs)) return false;
      return true;
    });
  }, [entries, actor, actionFilter, from, to]);

  const table = useTableState(filtered, {
    searchFields: ["action", "category", "severity", "actorUserId", "actorRole"],
    initialPageSize: 25
  });

  if (loading) return <SkeletonTable rows={5} columns={4} />;
  if (filtered.length === 0) {
    return (
      <EmptyState
        icon="📜"
        title="No audit entries match these filters"
        description="Adjust the category, severity, actor, or date range above. New events stream in as workspace activity occurs."
      />
    );
  }
  return (
    <div className="table-scroll">
      <TableToolbar
            hideExport
        search={table.search}
        setSearch={table.setSearch}
        page={table.page}
        setPage={table.setPage}
        pageSize={table.pageSize}
        setPageSize={table.setPageSize}
        totalPages={table.totalPages}
        totalRows={table.totalRows}
      />
      <table className="data-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Action</th>
            <th>Category</th>
            <th>Severity</th>
            <th>Actor</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {table.paged.map((e) => (
            <tr key={e.id}>
              <td className="soft">{new Date(e.occurredAt).toLocaleString()}</td>
              <td><code>{e.action}</code></td>
              <td><span className="pill pill-muted">{e.category ?? "—"}</span></td>
              <td>{severityPill(e.severity)}</td>
              <td className="soft">{e.actorUserId ?? "—"} <span className="muted">({e.actorRole})</span></td>
              <td>
                <button type="button" className="btn btn-sm" onClick={() => setDetail(e)}>View</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Audit() {
  const session = loadSession();
  const workspaceId = session?.workspaceId ?? "";
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>("");
  const [severity, setSeverity] = useState<string>("");
  const [actor, setActor] = useState<string>("");
  const [actionFilter, setActionFilter] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [detail, setDetail] = useState<Entry | null>(null);

  const load = useCallback(() => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    api.workspaceAudit(workspaceId, {
      category: category || undefined,
      severity: severity || undefined,
      limit: 200
    })
      .then((d) => setEntries(d.entries))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [workspaceId, category, severity]);

  useEffect(() => { load(); }, [load]);

  const onExportCsv = async () => {
    try {
      await api.downloadWorkspaceAuditCsv(workspaceId, {
        category: category || undefined,
        severity: severity || undefined,
        limit: 50000
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  if (!workspaceId) {
    return <div className="page"><div className="alert alert-error">No workspace in session.</div></div>;
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Audit log</h1>
          <p>Every policy-relevant action taken inside this workspace. Read-only, filterable by category and severity.</p>
        </div>
      </header>

      <div className="toolbar">
        <label>
          Category&nbsp;
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c || "All"}</option>)}
          </select>
        </label>
        <label>
          Severity&nbsp;
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s || "All"}</option>)}
          </select>
        </label>
        <label>
          Actor&nbsp;
          <input
            type="text"
            placeholder="user id / role"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            style={{ minWidth: 140 }}
          />
        </label>
        <label>
          Action&nbsp;
          <input
            type="text"
            placeholder="contains…"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={{ minWidth: 140 }}
          />
        </label>
        <label>
          From&nbsp;
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To&nbsp;
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="button" onClick={load}>Refresh</button>
        <button type="button" onClick={() => void onExportCsv()} disabled={entries.length === 0}>
          Export CSV
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <AuditTableSection
        entries={entries}
        loading={loading}
        actor={actor}
        actionFilter={actionFilter}
        from={from}
        to={to}
        setDetail={setDetail}
      />


      <Drawer
        open={detail != null}
        title="Audit entry"
        subtitle={detail ? <code>{detail.id}</code> : undefined}
        onClose={() => setDetail(null)}
        width={620}
        footer={<button type="button" className="btn" onClick={() => setDetail(null)}>Close</button>}
      >
        {detail && (
          <div style={{ display: "grid", gap: 8 }}>
            <div><strong>When:</strong> <span className="soft">{new Date(detail.occurredAt).toLocaleString()}</span></div>
            <div><strong>Action:</strong> <code>{detail.action}</code></div>
            <div><strong>Category:</strong> <span className="pill pill-muted">{detail.category ?? "—"}</span></div>
            <div><strong>Severity:</strong> {severityPill(detail.severity)}</div>
            <div><strong>Actor:</strong> <span className="pill pill-muted">{detail.actorRole}</span> {detail.actorUserId ? <code>{detail.actorUserId}</code> : null}</div>
            <div>
              <strong>Details:</strong>
              <pre style={{ background: "var(--surface-alt)", padding: 12, borderRadius: 6, overflow: "auto", maxHeight: 360 }}>
                {JSON.stringify(detail.details, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
